import React, {
  DetailedReactHTMLElement,
  MutableRefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import { Column } from '~common/components/column.component';
import {
  DropdownChildrenProps,
  DropdownProps,
  DropdownSelectMenuOption,
} from '~web/view/components/ui/form/dropdown-select-menu.component';
import { CheckMarkIcon } from '~web/view/components/ui/icon/check-mark.icon';
import { Span } from '~common/components/span.component';
import { NoScrollBar } from '~web/view/theme/utils';
import { Box } from '~common/components/box.component';

interface Props extends DropdownProps {
  parentRef: MutableRefObject<HTMLDivElement>;
}

export const DropdownSelectMenuDesktop: React.FunctionComponent<Props> = ({
  parentRef,
  currentValue,
  options,
  handleItemSelected,
  isOpen,
  yOffset = 15,
  xOffset = 0,
  rightAligned,
  topAligned,
  optionalDescription,
  onDropdownOpen,
  children,
}) => {
  const [menuOpened, setMenuOpened] = useState<boolean>(isOpen);
  const buttonRef = useRef<HTMLDivElement>(null);

  const blur = (): void => {
    if (menuOpened) {
      (buttonRef.current?.firstElementChild as HTMLElement)?.blur();
    }
  };

  const handleClickOutside = (event: MouseEvent): void => {
    if (parentRef.current && !parentRef.current.contains(event.target as Node)) {
      setMenuOpened(false);
      if (onDropdownOpen) {
        onDropdownOpen(false);
      }
    }
  };

  const onButtonClick = (event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    setMenuOpened(!menuOpened);
    if (onDropdownOpen) {
      onDropdownOpen(menuOpened);
    }
    blur();
  };

  const onItemSelected = (value: string): void => {
    handleItemSelected?.(value);

    setMenuOpened(false);
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  });

  useEffect(() => {
    if (onDropdownOpen) {
      onDropdownOpen(menuOpened);
    }
  }, [menuOpened]);

  return (
    <>
      <div ref={buttonRef}>
        {React.cloneElement<DropdownChildrenProps>(
          children as DetailedReactHTMLElement<DropdownChildrenProps, HTMLElement>,
          {
            onClick: onButtonClick,
            tabIndex: 0,
          },
        )}
      </div>
      {menuOpened && (
        <StyledMenu
          menuPositionOffset={buttonRef.current ? buttonRef.current.offsetHeight : 0}
          yOffset={yOffset}
          xOffset={xOffset}
          rightAligned={rightAligned}
          topAligned={topAligned}
          className={'dropdown-menu'}
        >
          {optionalDescription && (
            <StyledMenuItem className={'description'} disabled>
              <StyledDescriptionText>{optionalDescription}</StyledDescriptionText>
            </StyledMenuItem>
          )}
          {options.map((option: DropdownSelectMenuOption) => (
            <StyledMenuItem
              key={option.value}
              onClick={(event: React.MouseEvent<HTMLElement>): void => {
                event.stopPropagation();
                onItemSelected(option.value);
              }}
            >
              <StyledText>{option.name}</StyledText>
              <DecorationContainer>
                {option.renderDecoration?.()}
                {currentValue === option.value && <StyledCheckMark height={12} width={12} />}
              </DecorationContainer>
            </StyledMenuItem>
          ))}
        </StyledMenu>
      )}
    </>
  );
};

const StyledMenu = styled(
  ({ menuPositionOffset, yOffset, xOffset, rightAligned, topAligned, ...rest }) => (
    <Column {...rest} />
  ),
)<{
  menuPositionOffset: number;
  yOffset?: number;
  xOffset?: number;
  rightAligned?: boolean;
  topAligned?: boolean;
}>`
  position: absolute;
  z-index: ${(props): string => props.theme.zIndex.tooltip};
  border: 2px solid ${(props): string => props.theme.colors.gray.v3};
  border-radius: 16px;
  min-width: 100%;
  max-height: calc(42px * 6);
  overflow: hidden;
  overflow-y: auto;

  & button {
    font-weight: 500;
    font-size: 14px;
    line-height: 22px;
    letter-spacing: 0.3px;
    margin: 0;
    justify-content: center;
    min-height: 44px;
    min-width: 100%;
    border: 0 !important;
    background: ${(props): string => props.theme.colors.gray.v6};
    color: ${(props): string => props.theme.colors.white};
  }

  & button:hover {
    outline-color: transparent;
    background: ${(props): string => props.theme.colors.gray.v3};
  }

  & button.description:hover {
    outline-color: transparent;
    background: ${(props): string => props.theme.colors.gray.v6};
  }

  & span {
    user-select: none;
  }

  ${NoScrollBar};

  ${(props): FlattenSimpleInterpolation | null =>
    props.rightAligned
      ? css`
          right: ${props.xOffset}px;
        `
      : css`
          left: ${props.xOffset}px;
        `};

  ${(props): FlattenSimpleInterpolation | null =>
    props.topAligned
      ? css`
          bottom: ${props.menuPositionOffset + props.yOffset}px;
        `
      : css`
          top: ${props.menuPositionOffset + props.yOffset}px;
        `};
`;

const DecorationContainer = styled(Box)`
  align-items: center;
`;

const StyledMenuItem = styled.button`
  display: flex;
  && {
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    min-height: fit-content;
    white-space: nowrap;
    cursor: pointer;
  }
  &&:focus {
    outline-width: 0 !important;
  }
`;

const StyledCheckMark = styled(CheckMarkIcon)`
  margin-left: 12px;
`;

const StyledText = styled(Span)`
  text-align: left;
`;

const StyledDescriptionText = styled(StyledText)`
  padding-bottom: 8px;
  border-bottom: 1px solid
    ${(props): string => props.theme.colors.withAlpha(props.theme.colors.white)(0.2)};

  &:hover {
    background: transparent;
  }
`;



// WEBPACK FOOTER //
// /codebuild/output/src426716145/src/modules/web/src/js/view/components/ui/form/dropdown-select-menu-desktop.component.tsx